import React from 'react';
import PropTypes from 'prop-types';
import styles from './GenericPreview.module.scss';

const GenericPreview = ({
  user,
  content,
  title,
  hashtags = [],
  imageUrl,
  onRemoveImage,
}) => {
  const stripHtml = (html) => {
    if (!html) return '';
    // Convert block elements to newlines before stripping
    let text = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/div>/gi, '\n')
      .replace(/<div[^>]*>/gi, '')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ');

    // Strip remaining HTML tags
    const tmp = document.createElement('DIV');
    tmp.innerHTML = text;
    text = tmp.textContent || tmp.innerText || '';

    // Clean up excessive newlines
    return text.replace(/\n{3,}/g, '\n\n').trim();
  };

  const plainContent = stripHtml(content);
  const displayContent = plainContent.length > 300
    ? `${plainContent.slice(0, 300)}...`
    : plainContent;

  const getUserName = () => {
    if (!user) return 'User';
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.name || user.email || 'User';
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const firstName = user.first_name || user.name?.split(' ')[0] || '';
    const lastName = user.last_name || user.name?.split(' ')[1] || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  return (
    <div className={styles.genericPreview}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.avatar}>
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt={getUserName()} />
          ) : (
            <span>{getUserInitials()}</span>
          )}
        </div>
        <div className={styles.userInfo}>
          <div className={styles.userName}>{getUserName()}</div>
          <div className={styles.postMeta}>Just now</div>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {title && <div className={styles.title}>{title}</div>}
        <div className={styles.text}>{displayContent}</div>

        {/* Hashtags */}
        {hashtags.length > 0 && (
          <div className={styles.hashtags}>
            {hashtags.map((tag, index) => (
              <span key={index} className={styles.hashtag}>
                {tag.startsWith('#') ? tag : `#${tag}`}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Image */}
      {imageUrl && (
        <div className={styles.imageContainer}>
          <img src={imageUrl} alt="Preview" className={styles.image} />
          {onRemoveImage && (
            <button
              type="button"
              className={styles.removeButton}
              onClick={onRemoveImage}
              aria-label="Remove image"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Info Banner */}
      <div className={styles.infoBanner}>
        <svg viewBox="0 0 24 24" fill="currentColor" className={styles.infoIcon}>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
        </svg>
        <span>Select a platform to see a platform-specific preview</span>
      </div>
    </div>
  );
};

GenericPreview.propTypes = {
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    avatar_url: PropTypes.string,
  }),
  content: PropTypes.string,
  title: PropTypes.string,
  hashtags: PropTypes.arrayOf(PropTypes.string),
  imageUrl: PropTypes.string,
  onRemoveImage: PropTypes.func,
};

export default GenericPreview;
